import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { API_URL } from '../utils/api';
import { parisTodayKey, parisHour, dateToKey, keyToDate } from '../utils/parisTime';
import { useStore } from '../context/StoreContext';
import './ReservationPage.css';

registerLocale('fr', fr);

const validateName = (name) => {
  if (!name.trim()) return 'Le nom est obligatoire';
  if (name.trim().length < 2) return 'Au moins 2 caractères';
  if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(name)) return 'Lettres uniquement';
  return '';
};

const validatePhone = (phone) => {
  const clean = phone.replace(/\s/g, '');
  if (!clean) return 'Le téléphone est obligatoire';
  if (!/^0[1-9]\d{8}$/.test(clean)) return 'Format attendu : 06 12 34 56 78';
  return '';
};

const formatPhone = (value) =>
  value.replace(/\D/g, '').slice(0, 10).match(/.{1,2}/g)?.join(' ') || '';

function ReservationPage() {
  const navigate = useNavigate();
  const { customer } = useStore();
  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    date: parisTodayKey(),
    // Si le service du midi est déjà passé (heure de Paris), on propose le soir
    service: parisHour() >= 15 ? 'dinner' : 'lunch',
    time: '',
    guests: 2,
    notes: ''
  });
  const [availability, setAvailability] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Pré-remplissage depuis le compte fidélité
  useEffect(() => {
    if (customer) {
      setForm(prev => ({
        ...prev,
        customerName: prev.customerName || customer.name,
        customerPhone: prev.customerPhone || formatPhone(customer.phone)
      }));
    }
  }, [customer]);

  useEffect(() => {
    if (!form.date || !form.service) return;
    (async () => {
      try {
        const [availRes, slotsRes] = await Promise.all([
          fetch(`${API_URL}/reservations/availability?date=${form.date}&service=${form.service}&guests=${form.guests}`),
          fetch(`${API_URL}/reservations/timeslots?date=${form.date}&service=${form.service}`)
        ]);
        if (availRes.ok) setAvailability(await availRes.json());
        if (slotsRes.ok) {
          const slots = await slotsRes.json();
          setTimeSlots(slots);
          setForm(prev => (slots.includes(prev.time) ? prev : { ...prev, time: '' }));
        }
      } catch (error) {
        console.error('Erreur disponibilité:', error);
      }
    })();
  }, [form.date, form.service, form.guests]);

  const validateField = (field, value) => {
    const error = field === 'customerName' ? validateName(value) : validatePhone(value);
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ customerName: true, customerPhone: true });
    const nameOk = validateField('customerName', form.customerName);
    const phoneOk = validateField('customerPhone', form.customerPhone);
    if (!nameOk || !phoneOk) return;

    if (!availability?.available || availability.canFit === false) return;
    if (availability.canFit == null && form.guests > availability.availableSeats) {
      alert(`Il ne reste que ${availability.availableSeats} places. Appelez-nous au 07 49 11 01 86, nous trouverons une solution !`);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        setConfirmed(true);
        window.scrollTo({ top: 0 });
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.message || 'Erreur lors de la réservation');
      }
    } catch {
      alert('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <div className="container-page resa-confirmee">
        <div className="card card-pad resa-confirmee-carte">
          <span className="resa-confirmee-emoji">🎉</span>
          <h1>Table réservée !</h1>
          <p>Merci {form.customerName}, nous vous attendons.</p>

          <div className="resa-confirmee-recap">
            <div>
              <span>Date</span>
              <strong>
                {new Date(`${form.date}T12:00:00`).toLocaleDateString('fr-FR', {
                  weekday: 'long', day: 'numeric', month: 'long'
                })}
              </strong>
            </div>
            <div>
              <span>Service</span>
              <strong>{form.service === 'lunch' ? 'Midi' : 'Soir'}{form.time ? ` — ${form.time}` : ''}</strong>
            </div>
            <div>
              <span>Couverts</span>
              <strong>{form.guests} personne{form.guests > 1 ? 's' : ''}</strong>
            </div>
          </div>

          <p className="resa-confirmee-note">
            Un empêchement ? Prévenez-nous au <a href="tel:0749110186">07 49 11 01 86</a>.
          </p>
          <button className="btn btn-or" onClick={() => navigate('/')}>Retour à la carte</button>
        </div>
      </div>
    );
  }

  const isFormValid =
    form.customerName && form.customerPhone && form.date && form.time &&
    !errors.customerName && !errors.customerPhone &&
    availability?.available && availability?.canFit !== false;

  return (
    <div className="container-page resa-page">
      <div className="resa-entete">
        <h1>Réserver une table</h1>
        <p>Déjeuner ou dîner, votre table vous attend au Fief.</p>
      </div>

      <div className="resa-colonnes">
        <form className="card card-pad resa-form" onSubmit={handleSubmit}>
          <div className="resa-grille-3">
            <div className="field">
              <label>Date</label>
              <DatePicker
                selected={keyToDate(form.date)}
                onChange={(date) => setForm({ ...form, date: dateToKey(date) })}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                locale="fr"
                className="input"
                required
              />
            </div>

            <div className="field">
              <label>Service</label>
              <select
                className="input"
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value, time: '' })}
              >
                <option value="lunch">Midi</option>
                <option value="dinner">Soir</option>
              </select>
            </div>

            <div className="field">
              <label>Heure d'arrivée</label>
              <select
                className="input"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                disabled={timeSlots.length === 0}
                required
              >
                <option value="">{timeSlots.length ? 'Choisir…' : 'Aucun créneau'}</option>
                {timeSlots.map(slot => <option key={slot} value={slot}>{slot}</option>)}
              </select>
            </div>
          </div>

          {availability && (
            <div className={`resa-dispo ${availability.available && availability.canFit !== false ? 'ok' : 'ko'}`}>
              {availability.available && availability.canFit !== false
                ? <>✓ {availability.availableSeats} places disponibles sur {availability.totalSeats}</>
                : <>✕ {availability.reason || 'Complet pour ce service — appelez-nous au 07 49 11 01 86, nous trouverons une solution !'}</>}
            </div>
          )}

          <div className="field">
            <label>Nombre de personnes</label>
            <select
              className="input"
              value={form.guests}
              onChange={(e) => setForm({ ...form, guests: parseInt(e.target.value) })}
            >
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1} personne{i > 0 ? 's' : ''}</option>
              ))}
            </select>
          </div>

          <div className="resa-grille-2">
            <div className={`field ${touched.customerName && errors.customerName ? 'has-error' : ''}`}>
              <label>Nom</label>
              <input
                type="text"
                value={form.customerName}
                placeholder="Votre nom"
                onChange={(e) => {
                  setForm({ ...form, customerName: e.target.value });
                  if (touched.customerName) validateField('customerName', e.target.value);
                }}
                onBlur={() => { setTouched(t => ({ ...t, customerName: true })); validateField('customerName', form.customerName); }}
                required
              />
              {touched.customerName && errors.customerName && <p className="erreur">{errors.customerName}</p>}
            </div>

            <div className={`field ${touched.customerPhone && errors.customerPhone ? 'has-error' : ''}`}>
              <label>Téléphone</label>
              <input
                type="tel"
                value={form.customerPhone}
                placeholder="06 12 34 56 78"
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  setForm({ ...form, customerPhone: formatted });
                  if (touched.customerPhone) validateField('customerPhone', formatted);
                }}
                onBlur={() => { setTouched(t => ({ ...t, customerPhone: true })); validateField('customerPhone', form.customerPhone); }}
                required
              />
              {touched.customerPhone && errors.customerPhone && <p className="erreur">{errors.customerPhone}</p>}
            </div>
          </div>

          <div className="field">
            <label>Remarques (optionnel)</label>
            <textarea
              rows="3"
              value={form.notes}
              placeholder="Allergies, occasion spéciale, poussette…"
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <button type="submit" className="btn btn-or btn-lg btn-block" disabled={!isFormValid || loading}>
            {loading ? 'Réservation…' : 'Confirmer ma réservation'}
          </button>
        </form>

        <aside className="resa-infos">
          <div className="card card-pad">
            <h3>Bon à savoir</h3>
            <ul>
              <li>La table est à vous pour tout le service.</li>
              <li>Midi : 12h – 14h30 · Soir : 19h – 21h30.</li>
              <li>Vous choisissez et commandez sur place.</li>
              <li>Un empêchement ? Prévenez-nous, ça aide beaucoup.</li>
            </ul>
            <a href="tel:0749110186" className="btn btn-ghost-or btn-block">☎ 07 49 11 01 86</a>
          </div>
          <Link to="/" className="resa-lien-carte">Envie de découvrir la carte avant ? →</Link>
        </aside>
      </div>
    </div>
  );
}

export default ReservationPage;
